import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, IconButton, ListItem, ListItemText, MenuItem, Select, Typography, Button, Menu } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import {
	ACTION_GO_TO_SCENE,
	ACTION_GO_TO_URL,
	ACTION_JUMP_TO_TIME,
	widgetClickActions,
} from '../../../../../../../../../../constants/scenarioConstant';
import { useCustomStyles } from '../../../../../utils';

import GoToSceneAction from './actionOfEvent/GoToSceneAction';
import GoToUrlAction from './actionOfEvent/GoToUrlAction';
import JumpToTimeAction from './actionOfEvent/JumpToTimeAction';
import { Close, Delete as DeleteIcon } from '@material-ui/icons';

interface EventProps {
	type: string;
	defined: {
		label: string;
		icon: any;
		actions: { label: string; value: string }[];
	};
	data: any[];
	scenes: any[];
	editingScene: any;
	gotoUrlRef: React.Ref<HTMLInputElement> | undefined;
	openNewTabRef: React.Ref<HTMLInputElement> | undefined;
	handleDestinationSceneChange: () => {};
	handleGotoUrlWidgetChange: () => {};
	handleGotoUrlOpenNewTabChange: () => {};
	handleDeleteWidgetEvent: (_eventType: string) => {};
	handleAddActionToEvent: (_actionType: string, _eventType: string) => {};
	handleChangeActionType: (_oldActionType: string, _newActionType: string, _eventType: string) => {};
	handleDeleteAction: (_actionType: string, _eventType: string) => {};
	handleJumpToTimeMetaDataChange: (_value: string) => {};
}

const Event = ({
	type,
	defined,
	data,
	scenes,
	editingScene,
	gotoUrlRef,
	openNewTabRef,
	handleDestinationSceneChange,
	handleGotoUrlWidgetChange,
	handleGotoUrlOpenNewTabChange,
	handleDeleteWidgetEvent,
	handleAddActionToEvent,
	handleChangeActionType,
	handleDeleteAction,
	handleJumpToTimeMetaDataChange,
}: EventProps) => {
	const { t: translator } = useTranslation();
	const classes = useCustomStyles();

	const [open, setOpen] = useState(true);
	const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);

	const { icon, label, actions: availableActions } = defined;
	const selectedActions = data?.map((action) => action.type) || [];

	return (
		<div className="widget-event-wrapper">
			<ListItem
				className="widget-event-list-item-title"
				onClick={() => {
					setOpen(!open);
				}}
			>
				{icon}
				<ListItemText
					className="widget-event-title-text"
					primary={<Typography variant="body1">{translator(label) as string}</Typography>}
				/>
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						handleDeleteWidgetEvent(type);
					}}
				>
					<DeleteIcon htmlColor="#4d4d4d" />
				</IconButton>
			</ListItem>
			{open && selectedActions.length > 0 && <div className="widget-event-list-item-content-wrapper">
				{selectedActions.map((actionType, index) => {
					const isGoToSceneAction = actionType === ACTION_GO_TO_SCENE.value;
					const isGoToUrlAction = actionType === ACTION_GO_TO_URL.value;
					const isJumpToTimeAction = actionType === ACTION_JUMP_TO_TIME.value;
					const selectedValueOfAction = data?.[index]?.metadata;

					return (
						<React.Fragment key={index}>
							<div className="widget-event-list-item-content">
								<div style={{ display: 'flex', alignItems: 'center', flex: 1, width: '100%' }}>
									<div style={{ flex: 1 }}>
										<div style={{ display: 'flex', alignItems: 'center', flex: 1, width: '100%' }}>
											<FormControlLabel
												style={{ flex: 1, width: '100%' }}
												className={'scenario-chart__control'}
												label={translator('COMMON_ACTION') as string}
												onChange={(e) => {
													handleChangeActionType(actionType, (e.target as any).value, type);
												}}
												labelPlacement="start"
												control={
													<Select variant="outlined" value={actionType ?? 'none'} className={classes.input}>
														<MenuItem value={'none'} key={0}>
															-{translator('COMMON_CHOOSE') as string}-
														</MenuItem>
														{availableActions
															.filter((action) => action.value === actionType || !selectedActions.includes(action.value))
															.map((action) => (
																<MenuItem value={action.value} key={action.value}>
																	{translator(action.label) as string}
																</MenuItem>
															))}
													</Select>
												}
											/>
										</div>
										{isGoToSceneAction && (
											<GoToSceneAction
												scenes={scenes}
												editingScene={editingScene}
												metadata={selectedValueOfAction}
												handleDestinationSceneChange={handleDestinationSceneChange}
											/>
										)}

										{isGoToUrlAction && (
											<GoToUrlAction
												gotoUrlRef={gotoUrlRef}
												openNewTabRef={openNewTabRef}
												metadata={selectedValueOfAction}
												handleGotoUrlWidgetChange={handleGotoUrlWidgetChange}
												handleGotoUrlOpenNewTabChange={handleGotoUrlOpenNewTabChange}
											/>
										)}

										{isJumpToTimeAction && (
											<JumpToTimeAction
												metadata={selectedValueOfAction}
												selectedEvent={type}
												handleJumpToTimeMetaDataChange={handleJumpToTimeMetaDataChange}
											/>
										)}
									</div>
									<IconButton
										size="small"
										onClick={() => {
											handleDeleteAction(actionType, type);
										}}
										style={{ margin: '5px', alignSelf: 'baseline' }}
									>
										<Close htmlColor="#4d4d4d" />
									</IconButton>
								</div>

								{index === selectedActions.length - 1 && selectedActions.length < widgetClickActions.length ? (
									<div style={{ display: 'flex', justifyContent: 'center', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
										<div style={{ width: '100%', height: 1, borderBottom: '1px solid #E5E7EB', margin: "10px" }} />
										<Button
											startIcon={<AddIcon />}
											color="primary"
											variant="outlined"
											onClick={(e) => setActionMenuAnchor(e.currentTarget)}
										>
											{translator('COMMON_ADD_ACTION')}
										</Button>
									</div>
								) : <div style={{ width: "100%", borderBottom: '1px solid #E5E7EB', marginTop: "10px" }} />}
							</div>
						</React.Fragment>
					);
				})}
			</div>}

			{open && selectedActions.length === 0 && (<div className="widget-event-list-item-content-wrapper">
				<div className="widget-event-list-item-content">
					<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
						<FormControlLabel
							style={{ flex: 1, width: '100%' }}
							className={'scenario-chart__control'}
							label={translator('COMMON_ACTION') as string}
							onChange={(e) => {
								handleAddActionToEvent((e.target as any).value, type);
							}}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={'none'} className={classes.input}>
									<MenuItem value={'none'} key={0}>
										-{translator('COMMON_CHOOSE') as string}-
									</MenuItem>
									{availableActions
										.map((action) => (
											<MenuItem value={action.value} key={action.value}>
												{translator(action.label) as string}
											</MenuItem>
										))}
								</Select>
							}
						/>
					</div>
				</div>
			</div>)}

			<Menu
				anchorEl={actionMenuAnchor}
				onClose={() => setActionMenuAnchor(null)}
				open={Boolean(actionMenuAnchor)}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				{availableActions.filter(action => !selectedActions.includes(action.value)).map((action) => (
					<MenuItem
						key={action.value}
						onClick={() => {
							handleAddActionToEvent(action.value, type);
							setActionMenuAnchor(null);
						}}
					>
						{translator(action.label) as string}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
};

export default Event;
